import React from 'react';
import { Bone, SkeletonStatus, TextBone } from '../CampaignSkeleton/CampaignSkeleton';

/** Rows drawn while the contacts load: what the bounded table shows without scrolling. */
const ROWS = 8;
/** The results table's usual columns (phone, outcome, status, subagent, conversation). */
const COLUMNS = 5;

/** One block of rows as bones: the index, then one bar per column, row after row. */
const Rows: React.FC = () => (
  <div className="campaign-results__skeleton-rows">
    {Array.from({ length: ROWS }, (_, row) => (
      <div key={row} className="campaign-results__skeleton-row">
        <Bone width={16} height={12} />
        {Array.from({ length: COLUMNS }, (_, c) => (
          // Widths vary a little by row, as text does.
          <Bone key={c} width={`${60 + ((row * 7 + c * 13) % 30)}%`} height={12} />
        ))}
      </div>
    ))}
  </div>
);

/**
 * The rows while the first page loads, once the columns are known: drawn in
 * the table's empty cell, under its real header, in place of "No contacts".
 */
export const ContactsRowsSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <SkeletonStatus label={label} testId="contacts-rows-skeleton">
    <Rows />
  </SkeletonStatus>
);

/**
 * The results table before its columns are known (VOX-319): the toolbar — the
 * state select, the search, the three switches and the count — then the
 * bordered table with its header and rows, at the sizes `CampaignResultsTable`
 * draws them.
 */
export const ContactsSkeleton: React.FC<{ label: string; full?: boolean }> = ({ label, full }) => (
  <SkeletonStatus
    label={label}
    className={`campaign-results${full ? ' campaign-results--full' : ''}`}
    testId="contacts-skeleton"
  >
    <div className="campaign-results__toolbar">
      {/* Wrapped, so the controls' own widths (and their phone rule) apply. */}
      <span className="campaign-results__state">
        <Bone height="var(--silia-ctl-height)" />
      </span>
      <span className="campaign-results__search">
        <Bone height="var(--silia-ctl-height)" />
      </span>
      {[0, 1, 2].map((i) => (
        <Bone key={i} width="var(--silia-ctl-height)" height="var(--silia-ctl-height)" />
      ))}
      <span className="campaign-results__count">
        <TextBone width={140} size={12} lineHeight={20} />
      </span>
    </div>
    <div className="data-views-table-container campaign-results__table">
      <div className="campaign-results__skeleton-head">
        <Bone width={16} height={12} />
        {Array.from({ length: COLUMNS }, (_, c) => (
          <Bone key={c} width="50%" height={12} />
        ))}
      </div>
      <Rows />
    </div>
    <TextBone width={160} size={12} lineHeight={20} />
  </SkeletonStatus>
);
